<!--
  Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
  This information is confidential and proprietary to GM Global Technology
  Operations LLC and may not be used, modified, copied or distributed.
  -->

<br/>
<h1 class="page-header">Create ICDs</h1>

@if (permission()) {
  <div class="icd-workflow">
  <div [hidden]="!showTemplateSelectionStep">
      @if (isAggregator) {
        <!-- No template step for aggregators: every matrix row is always included, so the user picks the
             matrix itself. Single-select, because an ICD is built from exactly one matrix. -->
        <ag-grid-angular class="ag-theme-balham create-icds-grid"
                         [columnDefs]="aggregatorInventoryCols"
                         [rowData]="matrixInventory"
                         [defaultColDef]="defaultColDef" [enableBrowserTooltips]="true"
                         [overlayLoadingTemplate]="matrixInventoryLoadingOverlay"
                         rowSelection="single"
                         (gridReady)="onGridReady($event)"
                         (rowDataUpdated)="onFilterChanged()"
                         (selectionChanged)="onAggregatorMatrixSelectionChanged()">
        </ag-grid-angular>
<br/>
        <div class="button-row">
          @if (canExport) {
            <!-- Both exports key off the selected matrix scope, so neither is offered until one is picked. -->
            <p-button icon="pi pi-download" (onClick)="exportReport()" [disabled]="!aggregatorScopeComplete"
                      label="Download Report" class="gm-btn-primary mr-2"></p-button>
            <p-button icon="pi pi-download" (onClick)="exportHistory()" [disabled]="!aggregatorScopeComplete"
                      label="Download History" class="gm-btn-primary mr-2"></p-button>
          }
          <p-button label="Start ICD Creation" [disabled]="!canStartIcdCreation"
                    (click)="startIcdCreation()"></p-button>
        </div>
      } @else {
        <ag-grid-angular class="ag-theme-balham create-icds-grid"
                         [columnDefs]="inventoryCols"
                         [rowData]="templateInventory"
                         [defaultColDef]="defaultColDef"
                         [overlayLoadingTemplate]="templateInventoryLoadingOverlay"
                         rowSelection="multiple"
                         (gridReady)="onGridReady($event)"
                         (rowDataUpdated)="onFilterChanged()"
                         (selectionChanged)="onTemplateInventorySelectionChanged()">
        </ag-grid-angular>
<br/>
        <div class="button-row">
          @if (canExport) {
            <p-button icon="pi pi-download" (onClick)="exportReport()" [disabled]="selectedTemplateIds.length === 0"
                      label="Download Report" class="gm-btn-primary mr-2"></p-button>
            <p-button icon="pi pi-download" (onClick)="exportHistory()" [disabled]="selectedTemplateIds.length === 0"
                      label="Download History" class="gm-btn-primary mr-2"></p-button>
          }
          <p-button label="Start ICD Creation" [disabled]="!canStartIcdCreation" (click)="startIcdCreation()"></p-button>
        </div>

        @if (metadata['cadence'] === 'Multiple') {
          <p-message severity="warn"
                     text="Selected templates span multiple cadences. Select a single cadence context before creating ICDs."></p-message>
        }
      }
  </div>

    @if (createIcdSuccessVisible) {
      <p-message severity="success"
                 [closable]="true"
                 (onClose)="dismissCreateIcdSuccess()">
        <div class="create-icd-success-content">
          <span>{{ createIcdSuccessMessage }}</span>
          @if (createIcdSuccessLinkUrl) {
            <a [href]="createIcdSuccessLinkUrl"
               target="_blank"
               rel="noopener noreferrer">{{ createIcdSuccessLinkLabel }}</a>
          }
        </div>
      </p-message>
    }

    @if (showCreateIcdMetadataForm) {
      <div class="metadata-box">
        <h3>ICD Metadata</h3>
        <div class="metadata-form">
          <div class="metadata-row">
            <div class="metadata-cell" data-testid="metadata-icdType">
              <label class="metadata-label">*ICD Type:</label>
              <p-select
                class="metadata-input"
                [options]="icdTypeOptions"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="metadata['icdType']"></p-select>
            </div>
            <div class="metadata-cell" data-testid="metadata-icdSubType">
              <label class="metadata-label">*ICD Sub-Type:</label>
              <p-select
                class="metadata-input"
                [options]="icdSubTypeOptions"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="metadata['icdSubType']"></p-select>
            </div>
          </div>

          <div class="metadata-row metadata-checkbox-row">
            <div class="metadata-cell">
              <label class="metadata-label"></label>
              <div class="metadata-checkbox-group">
                <div class="metadata-checkbox-item">
                  <p-checkbox
                    inputId="metadata-parentIcd"
                    data-testid="metadata-parentIcd"
                    [binary]="true"
                    [(ngModel)]="metadata['parentIcd']"></p-checkbox>
                  <label for="metadata-parentIcd">Parent ICD</label>
                </div>
                @if (isAggregator) {
                  <!-- Readonly and on by default: the per-pin IO Type Designator, SW Channel ID and port
                       name that the aggregator signal table supplies only exist on an EIO ICD, so turning
                       this off would silently drop them. -->
                  <div class="metadata-checkbox-item">
                    <p-checkbox
                      inputId="metadata-eioIcd"
                      data-testid="metadata-eioIcd"
                      [binary]="true"
                      [disabled]="true"
                      [(ngModel)]="metadata['eioIcd']"></p-checkbox>
                    <label for="metadata-eioIcd">EIO ICD</label>
                  </div>
                }
              </div>
            </div>
            <div class="metadata-cell">
              <label class="metadata-label"></label>
              <div class="metadata-checkbox-group">
                <div class="metadata-checkbox-item metadata-cell-spacer"></div>
              </div>
            </div>
          </div>

          <div class="metadata-row metadata-row-top">
            <div class="metadata-cell" [class.readonly-metadata-field]="!isAggregator">
              <label class="metadata-label">*Program:</label>
              @if (isAggregator) {
                <div class="metadata-field">
                  <p-select class="metadata-input" [options]="programOptions()" optionLabel="label" optionValue="value"
                            [(ngModel)]="selectedProgram" [filter]="true" [showClear]="true"
                            [invalid]="!!programTypeMismatchWarning()"
                            placeholder="Select" data-testid="metadata-program"></p-select>
                  @if (programTypeMismatchWarning()) {
                    <p-message class="program-type-mismatch-message"
                               severity="error"
                               size="small"
                               variant="simple"
                               data-testid="program-type-mismatch-warning"
                               [text]="programTypeMismatchWarning()"></p-message>
                  }
                </div>
              } @else {
                <input class="metadata-input" pInputText data-testid="metadata-program" [(ngModel)]="metadata['program']" [disabled]="true"/>
              }
            </div>
            <div class="metadata-cell" [class.readonly-metadata-field]="!isAggregator">
              <label class="metadata-label">*Model Year:</label>
              @if (isAggregator) {
                <p-select class="metadata-input" [options]="modelYearOptions" optionLabel="label" optionValue="value"
                          [(ngModel)]="metadata['modelYear']" [filter]="true" [showClear]="true"
                          placeholder="Select" data-testid="metadata-modelYear"></p-select>
              } @else {
                <input class="metadata-input" pInputText data-testid="metadata-modelYear" [(ngModel)]="metadata['modelYear']" [disabled]="true"/>
              }
            </div>
          </div>

          <div class="metadata-row">
            <div class="metadata-cell" [class.readonly-metadata-field]="!isAggregator">
              <label class="metadata-label">*RPO:</label>
              <input class="metadata-input" pInputText data-testid="metadata-rpo" [(ngModel)]="metadata['rpo']"
                     [disabled]="!isAggregator"/>
            </div>
            <div class="metadata-cell" [class.readonly-metadata-field]="!isAggregator">
              <label class="metadata-label">Additional RPOs:</label>
              <input class="metadata-input" pInputText data-testid="metadata-additionalRpos" [(ngModel)]="metadata['additionalRpos']"
                     [disabled]="!isAggregator"/>
            </div>
          </div>

          <div class="metadata-row">
            @if (isAggregator) {
              <div class="metadata-cell readonly-metadata-field">
                <label class="metadata-label">Keyword:</label>
                <input class="metadata-input" pInputText data-testid="metadata-keyword" [(ngModel)]="metadata['keyword']"
                       [disabled]="true"/>
              </div>
            } @else {
              <div class="metadata-cell readonly-metadata-field">
                <label class="metadata-label">*Build Phase:</label>
                <input class="metadata-input" pInputText data-testid="metadata-buildPhase" [(ngModel)]="metadata['buildPhase']"
                       [disabled]="true"/>
              </div>
            }
            <div class="metadata-cell metadata-cell-spacer"></div>
          </div>

          <div class="metadata-row">
            <div class="metadata-cell readonly-metadata-field">
              <label class="metadata-label">Object Code:</label>
              <input class="metadata-input" pInputText data-testid="metadata-objectCode" [(ngModel)]="metadata['objectCode']" [disabled]="true"/>
            </div>
            <div class="metadata-cell readonly-metadata-field">
              <label class="metadata-label">Object Code Inst:</label>
              <input class="metadata-input" pInputText data-testid="metadata-objectCodeInst" [(ngModel)]="metadata['objectCodeInst']" [disabled]="true"/>
            </div>
          </div>

          <div class="metadata-row">
            <div class="metadata-cell readonly-metadata-field">
              <label class="metadata-label">VPPS:</label>
              <input class="metadata-input" pInputText data-testid="metadata-vpps" [(ngModel)]="metadata['vpps']" [disabled]="true"/>
            </div>
            <div class="metadata-cell readonly-metadata-field">
              <label class="metadata-label">UPC:</label>
              <input class="metadata-input" pInputText data-testid="metadata-upc" [(ngModel)]="metadata['upc']" [disabled]="true"/>
            </div>
          </div>

          <div class="metadata-row">
            <div class="metadata-cell readonly-metadata-field">
              <label class="metadata-label">FNA:</label>
              <input class="metadata-input" pInputText data-testid="metadata-fna" [(ngModel)]="metadata['fna']" [disabled]="true"/>
            </div>
            <div class="metadata-cell metadata-cell-spacer"></div>
          </div>

          <div class="metadata-row">
            <div class="metadata-cell">
              <label class="metadata-label">*ICDe Revision:</label>
              <p-select
                class="metadata-input"
                [options]="revisionOptions"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="metadata['revision']"></p-select>
            </div>
            <div class="metadata-cell metadata-cell-spacer"></div>
          </div>

          <div class="metadata-row">
            <div class="metadata-cell">
              <label class="metadata-label">*Device Variant:</label>
              <p-select
                class="metadata-input"
                [options]="deviceVariantOptions"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="metadata['deviceVariant']"></p-select>
            </div>
            <div class="metadata-cell">
              <label class="metadata-label">Device Variant Desc.:</label>
              <textarea class="metadata-input" rows="2" pInputTextarea [(ngModel)]="metadata['deviceVariantDesc']"></textarea>
            </div>
          </div>

          <div class="metadata-row">
            <div class="metadata-cell">
              <label class="metadata-label">*Security Classifier:</label>
              <p-select
                class="metadata-input"
                [options]="securityClassifierOptions"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="metadata['securityClassifier']"></p-select>
            </div>
            <div class="metadata-cell metadata-cell-spacer"></div>
          </div>

          <div class="metadata-row">
            <div class="metadata-cell">
              <label class="metadata-label">Child Object Code(s):</label>
              <input class="metadata-input" pInputText [(ngModel)]="metadata['childObjectCodes']"/>
            </div>
            <div class="metadata-cell metadata-cell-spacer"></div>
          </div>

          <div class="metadata-row">
            <div class="metadata-cell">
              <label class="metadata-label">Milestone:</label>
              <p-select
                class="metadata-input"
                [options]="buildStatusOptions"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="metadata['buildStatus']"></p-select>
            </div>
            <div class="metadata-cell readonly-metadata-field">
              <label class="metadata-label">*Cadence:</label>
              <input class="metadata-input"
                     pInputText
                     data-testid="metadata-cadence"
                     [(ngModel)]="metadata['cadence']"
                     [disabled]="true"/>
            </div>
          </div>

          <div class="metadata-row">
            <div class="metadata-cell readonly-metadata-field">
              <label class="metadata-label">*Architecture:</label>
              <input class="metadata-input"
                     pInputText
                     data-testid="metadata-platform"
                     [(ngModel)]="metadata['platform']"
                     [disabled]="true"/>
            </div>
            <div class="metadata-cell metadata-cell-spacer"></div>
          </div>

          <div class="metadata-row">
            <div class="metadata-cell">
              <label class="metadata-label">File URL:</label>
              <input class="metadata-input" pInputText [(ngModel)]="metadata['spLocation']"/>
            </div>
            <div class="metadata-cell metadata-cell-spacer"></div>
          </div>

          <div class="metadata-row">
            <div class="metadata-cell">
              <label class="metadata-label">Diode IFSM:</label>
              <input class="metadata-input" pInputText [(ngModel)]="metadata['diodeIFSM']"/>
            </div>
            <div class="metadata-cell">
              <label class="metadata-label">Diode VRRM:</label>
              <input class="metadata-input" pInputText [(ngModel)]="metadata['diodeVRRM']"/>
            </div>
          </div>

          <div class="metadata-row">
            <div class="metadata-cell">
              <label class="metadata-label">Is this component expected to operate normally during auto stop and auto start modes?</label>
              <p-select
                class="metadata-input"
                [options]="operateNormallyOptions"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="metadata['operateNormally']"></p-select>
            </div>
            <div class="metadata-cell">
              <label class="metadata-label">Has this component been tested and met the GMW3172 Auto Start voltage profile requirements, remaining fully functional?</label>
              <p-select
                class="metadata-input"
                [options]="fullyFunctionalOptions"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="metadata['fullyFunctional']"></p-select>
            </div>
          </div>

          <div class="metadata-row">
            <div class="metadata-cell">
              <label class="metadata-label">*Is this Component Sourced?</label>
              <p-select
                class="metadata-input"
                [options]="componentSourcedOptions"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="metadata['icdSourced']"></p-select>
            </div>
            @if (metadata['icdSourced'] === 'Y') {
              <div class="metadata-cell">
                <label class="metadata-label">Component Sourced Date:</label>
                <input class="metadata-input"
                       type="date"
                       pInputText
                       [(ngModel)]="metadata['icdSourcedDate']"
                       [disabled]="metadata['icdSourced'] !== 'Y'"/>
              </div>
            } @else {
              <div class="metadata-cell metadata-cell-spacer"></div>
            }
          </div>

          <div class="metadata-row">
            <div class="metadata-cell">
              <label class="metadata-label" for="rcd-interf-identified-date">Stage 0 Due Date:</label>
              <input class="metadata-input" type="date" pInputText
                     id="rcd-interf-identified-date"
                     data-testid="rcd-interf-identified-date"
                     [(ngModel)]="metadata['rcdInterfIdentifiedDate']"/>
            </div>
            <div class="metadata-cell">
              <label class="metadata-label" for="rcd-conn-date">Stage 1 Due Date:</label>
              <input class="metadata-input" type="date" pInputText
                     id="rcd-conn-date"
                     data-testid="rcd-conn-date"
                     [(ngModel)]="metadata['rcdConnDate']"/>
            </div>
          </div>

          <div class="metadata-row">
            <div class="metadata-cell">
              <label class="metadata-label" for="rcd-interf-agreed-date">Stage 2 Due Date:</label>
              <input class="metadata-input" type="date" pInputText
                     id="rcd-interf-agreed-date"
                     data-testid="rcd-interf-agreed-date"
                     [(ngModel)]="metadata['rcdInterfAgreedDate']"/>
            </div>
            <div class="metadata-cell">
              <label class="metadata-label" for="rcd-iver-date">Stage 3 Due Date:</label>
              <input class="metadata-input" type="date" pInputText
                     id="rcd-iver-date"
                     data-testid="rcd-iver-date"
                     [(ngModel)]="metadata['rcdIverDate']"/>
            </div>
          </div>

          <div class="metadata-row">
            <div class="metadata-cell">
              <label class="metadata-label" for="rcd-sop-date">Stage 4 Due Date:</label>
              <input class="metadata-input" type="date" pInputText
                     id="rcd-sop-date"
                     data-testid="rcd-sop-date"
                     [(ngModel)]="metadata['rcdSopDate']"/>
            </div>
            <div class="metadata-cell metadata-cell-spacer"></div>
          </div>

          <div class="icd-users-section">
            <h3 class="icd-users-heading">ICD Users</h3>
            <table class="icd-users-table">
              <colgroup>
                <col class="role-col">
                <col class="pref-col">
                <col class="email-col">
              </colgroup>
              <thead>
              <tr>
                <th>Role</th>
                <th>Email Preferences</th>
                <th>Additional Emails</th>
              </tr>
              </thead>
              <tbody>
              <tr>
                <td class="role-label"><label>*All Users on ICD</label></td>
                <td class="email-pref-cell">
                  <div class="pref-toggle-group">
                    <button type="button" class="pref-toggle-btn"
                            [class.pref-toggle-btn-active]="isEmailPreferenceOn('allUsersEmailStatus')"
                            (click)="setEmailPreferenceStatus('allUsersEmailStatus', 'ON')">
                      ON
                    </button>
                    <button type="button" class="pref-toggle-btn"
                            [class.pref-toggle-btn-active]="!isEmailPreferenceOn('allUsersEmailStatus')"
                            (click)="setEmailPreferenceStatus('allUsersEmailStatus', 'OFF')">
                      OFF
                    </button>
                  </div>
                </td>
                <td><textarea class="additional-email-input" pInputTextarea rows="2" [(ngModel)]="metadata['allUsersEmail']"></textarea></td>
              </tr>
              <tr>
                <td class="role-cell">
                  <label>*ICDe Engineer:</label>
                  <app-auto-search class="role-search" [showMeButton]="true"
                                   [initialUser]="getMetadataUserInitialValue('icdeEngineer')"
                                   (userSelected)="onMetadataUserSelected('icdeEngineer', $event)"></app-auto-search>
                </td>
                <td class="email-pref-cell">
                  <div class="pref-toggle-group">
                    <button type="button" class="pref-toggle-btn"
                            [class.pref-toggle-btn-active]="isEmailPreferenceOn('icdeEngineerEmailStatus')"
                            (click)="setEmailPreferenceStatus('icdeEngineerEmailStatus', 'ON')">
                      ON
                    </button>
                    <button type="button" class="pref-toggle-btn"
                            [class.pref-toggle-btn-active]="!isEmailPreferenceOn('icdeEngineerEmailStatus')"
                            (click)="setEmailPreferenceStatus('icdeEngineerEmailStatus', 'OFF')">
                      OFF
                    </button>
                  </div>
                </td>
                <td><textarea class="additional-email-input" pInputTextarea rows="2" [(ngModel)]="metadata['icdeEngineerEmail']"></textarea></td>
              </tr>
              <tr>
                <td class="role-cell">
                  <label>*Device DRE:</label>
                  <app-auto-search class="role-search" [showMeButton]="true"
                                   [initialUser]="getMetadataUserInitialValue('deviceDre')"
                                   (userSelected)="onMetadataUserSelected('deviceDre', $event)"></app-auto-search>
                </td>
                <td class="email-pref-cell">
                  <div class="pref-toggle-group">
                    <button type="button" class="pref-toggle-btn"
                            [class.pref-toggle-btn-active]="isEmailPreferenceOn('deviceDreEmailStatus')"
                            (click)="setEmailPreferenceStatus('deviceDreEmailStatus', 'ON')">
                      ON
                    </button>
                    <button type="button" class="pref-toggle-btn"
                            [class.pref-toggle-btn-active]="!isEmailPreferenceOn('deviceDreEmailStatus')"
                            (click)="setEmailPreferenceStatus('deviceDreEmailStatus', 'OFF')">
                      OFF
                    </button>
                  </div>
                </td>
                <td><textarea class="additional-email-input" pInputTextarea rows="2" [(ngModel)]="metadata['deviceDreEmail']"></textarea></td>
              </tr>
              <tr>
                <td class="role-cell">
                  <label>Delegate Device DRE:</label>
                  <app-auto-search class="role-search" [showMeButton]="true"
                                   [initialUser]="getMetadataUserInitialValue('delegateDeviceDre')"
                                   (userSelected)="onMetadataUserSelected('delegateDeviceDre', $event)"></app-auto-search>
                </td>
                <td class="approval-cell">
                  <label class="approval-label">Delegate Device DRE Approval Required</label>
                  <p-checkbox [binary]="true" [(ngModel)]="metadata['delegateDeviceDreApprovalRequired']"></p-checkbox>
                </td>
                <td class="approval-empty-cell"></td>
              </tr>
              <tr>
                <td class="role-cell">
                  <label>*Elec Architect:</label>
                  <app-auto-search class="role-search" [showMeButton]="true"
                                   [initialUser]="getMetadataUserInitialValue('electricalArchitect')"
                                   (userSelected)="onMetadataUserSelected('electricalArchitect', $event)"></app-auto-search>
                </td>
                <td class="email-pref-cell">
                  <div class="pref-toggle-group">
                    <button type="button" class="pref-toggle-btn"
                            [class.pref-toggle-btn-active]="isEmailPreferenceOn('electricalArchitectEmailStatus')"
                            (click)="setEmailPreferenceStatus('electricalArchitectEmailStatus', 'ON')">
                      ON
                    </button>
                    <button type="button" class="pref-toggle-btn"
                            [class.pref-toggle-btn-active]="!isEmailPreferenceOn('electricalArchitectEmailStatus')"
                            (click)="setEmailPreferenceStatus('electricalArchitectEmailStatus', 'OFF')">
                      OFF
                    </button>
                  </div>
                </td>
                <td><textarea class="additional-email-input" pInputTextarea rows="2" [(ngModel)]="metadata['electricalArchitectEmail']"></textarea></td>
              </tr>
              <tr>
                <td class="role-cell">
                  <label>Delegate Elec Architect:</label>
                  <app-auto-search class="role-search" [showMeButton]="true"
                                   [initialUser]="getMetadataUserInitialValue('delegateElectricalArchitect')"
                                   (userSelected)="onMetadataUserSelected('delegateElectricalArchitect', $event)"></app-auto-search>
                </td>
                <td class="approval-cell">
                  <label class="approval-label">Delegate Electrical Architect Approval Required</label>
                  <p-checkbox [binary]="true" [(ngModel)]="metadata['delegateElectricalArchitectApprovalRequired']"></p-checkbox>
                </td>
                <td class="approval-empty-cell"></td>
              </tr>
              <tr>
                <td class="role-cell">
                  <label>Device VSE:</label>
                  <app-auto-search class="role-search" [showMeButton]="true"
                                   [initialUser]="getMetadataUserInitialValue('deviceVse')"
                                   (userSelected)="onMetadataUserSelected('deviceVse', $event)"></app-auto-search>
                </td>
                <td class="email-pref-cell">
                  <div class="pref-toggle-group">
                    <button type="button" class="pref-toggle-btn"
                            [class.pref-toggle-btn-active]="isEmailPreferenceOn('deviceVseEmailStatus')"
                            (click)="setEmailPreferenceStatus('deviceVseEmailStatus', 'ON')">
                      ON
                    </button>
                    <button type="button" class="pref-toggle-btn"
                            [class.pref-toggle-btn-active]="!isEmailPreferenceOn('deviceVseEmailStatus')"
                            (click)="setEmailPreferenceStatus('deviceVseEmailStatus', 'OFF')">
                      OFF
                    </button>
                  </div>
                </td>
                <td><textarea class="additional-email-input" pInputTextarea rows="2" [(ngModel)]="metadata['deviceVseEmail']"></textarea></td>
              </tr>
              <tr>
                <td class="role-cell">
                  <label>Electrical VSE:</label>
                  <app-auto-search class="role-search" [showMeButton]="true"
                                   [initialUser]="getMetadataUserInitialValue('electricalVse')"
                                   (userSelected)="onMetadataUserSelected('electricalVse', $event)"></app-auto-search>
                </td>
                <td class="email-pref-cell">
                  <div class="pref-toggle-group">
                    <button type="button" class="pref-toggle-btn"
                            [class.pref-toggle-btn-active]="isEmailPreferenceOn('electricalVseEmailStatus')"
                            (click)="setEmailPreferenceStatus('electricalVseEmailStatus', 'ON')">
                      ON
                    </button>
                    <button type="button" class="pref-toggle-btn"
                            [class.pref-toggle-btn-active]="!isEmailPreferenceOn('electricalVseEmailStatus')"
                            (click)="setEmailPreferenceStatus('electricalVseEmailStatus', 'OFF')">
                      OFF
                    </button>
                  </div>
                </td>
                <td><textarea class="additional-email-input" pInputTextarea rows="2" [(ngModel)]="metadata['electricalVseEmail']"></textarea></td>
              </tr>
              <tr>
                <td class="role-cell">
                  <label>*Device EGM:</label>
                  <app-auto-search class="role-search" [showMeButton]="true"
                                   [initialUser]="getMetadataUserInitialValue('deviceEgm')"
                                   (userSelected)="onMetadataUserSelected('deviceEgm', $event)"></app-auto-search>
                </td>
                <td class="email-pref-cell">
                  <div class="pref-toggle-group">
                    <button type="button" class="pref-toggle-btn"
                            [class.pref-toggle-btn-active]="isEmailPreferenceOn('deviceEgmEmailStatus')"
                            (click)="setEmailPreferenceStatus('deviceEgmEmailStatus', 'ON')">
                      ON
                    </button>
                    <button type="button" class="pref-toggle-btn"
                            [class.pref-toggle-btn-active]="!isEmailPreferenceOn('deviceEgmEmailStatus')"
                            (click)="setEmailPreferenceStatus('deviceEgmEmailStatus', 'OFF')">
                      OFF
                    </button>
                  </div>
                </td>
                <td><textarea class="additional-email-input" pInputTextarea rows="2" [(ngModel)]="metadata['deviceEgmEmail']"></textarea></td>
              </tr>
              <tr>
                <td class="role-cell">
                  <label>*Architect EGM:</label>
                  <app-auto-search class="role-search" [showMeButton]="true"
                                   [initialUser]="getMetadataUserInitialValue('architectEgm')"
                                   (userSelected)="onMetadataUserSelected('architectEgm', $event)"></app-auto-search>
                </td>
                <td class="email-pref-cell">
                  <div class="pref-toggle-group">
                    <button type="button" class="pref-toggle-btn"
                            [class.pref-toggle-btn-active]="isEmailPreferenceOn('architectEgmEmailStatus')"
                            (click)="setEmailPreferenceStatus('architectEgmEmailStatus', 'ON')">
                      ON
                    </button>
                    <button type="button" class="pref-toggle-btn"
                            [class.pref-toggle-btn-active]="!isEmailPreferenceOn('architectEgmEmailStatus')"
                            (click)="setEmailPreferenceStatus('architectEgmEmailStatus', 'OFF')">
                      OFF
                    </button>
                  </div>
                </td>
                <td><textarea class="additional-email-input" pInputTextarea rows="2" [(ngModel)]="metadata['architectEgmEmail']"></textarea></td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      @if (showCreateIcdActionButtons) {
        <!-- Spelled out rather than left to the greyed-out button: a disabled p-button swallows the click,
             so an on-click error would never be seen, and this form is long enough that the one blank
             field holding Create back can easily be off screen. -->
        @if (!createIcdMetadataComplete) {
          <p-message severity="warn"
                     data-testid="missing-required-fields"
                     [text]="missingRequiredMetadataMessage"></p-message>
        }
        <div class="button-row">
          <p-button label="Create ICD(s)"
                    [disabled]="!canCreateIcds"
                    (click)="createIcds()"></p-button>
          <!-- Stays on canSubmitCreateIcd: backing out must not depend on the form being complete. -->
          <p-button label="Cancel" severity="warn"
                    [disabled]="!canSubmitCreateIcd"
                    (click)="icdCreationStarted = false"></p-button>
        </div>
      }
    }

    <!-- Outside the metadata form on purpose: the form is hidden while a create runs (a large aggregator
         matrix takes a while), so a progress indicator nested inside it would disappear exactly when it
         is needed. -->
    @if (createIcdSubmissionInProgress) {
      <div class="create-icd-progress" data-testid="create-icd-progress">
        <div class="create-icd-progress-label">Creating ICD(s)...</div>
        <div class="create-icd-progress-track">
          <div class="create-icd-progress-bar"></div>
        </div>
      </div>
    }

    @if (createIcdErrorVisible) {
      <p-message severity="error"
                 [closable]="true"
                 (onClose)="dismissCreateIcdError()"
                 [text]="createIcdErrorMessage"></p-message>
    }
  </div>
}
